import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {fetchContainerMeasurements} from 'samples/grids/hooks/useContainerMeasurements';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {ArchivedContentsQuery} from 'samples/plate-maps/fill-plate-modal/__generated/ArchivedContentsQuery';
import {
  InaccessibleContentsQuery,
  InaccessibleContentsQueryVariables,
} from 'samples/plate-maps/fill-plate-modal/__generated/InaccessibleContentsQuery';
import FillPlateSearchInput from 'samples/plate-maps/fill-plate-modal/FillPlateSearchInput';
import OrderableReagentList from 'samples/plate-maps/fill-plate-modal/OrderableReagentList';
import {PlateModalMachineAction} from 'samples/plate-maps/fill-plate-modal/PlateModalMachineProvider';
import ReagentChip from 'samples/plate-maps/fill-plate-modal/ReagentChip';
import WellAssignmentMethodSelector from 'samples/plate-maps/fill-plate-modal/WellAssignmentMethodSelector';
import {
  getProcedureTestOrderConfig,
  TOO_MANY_DILUTION_FACTORS_SELECTED_WELL_MESSAGE,
  TOO_MANY_TRIAL_SAMPLES_SELECTED_WELL_MESSAGE,
  NO_TRIAL_SAMPLES_ON_PLATE_DESIGN_MESSAGE,
  maybeHydrateSourceWithFetchedTestOrders,
} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {PatternParametersInput} from 'samples/plate-maps/pattern-controls/AssignWellsByPattern';
import PatternAssignmentIntegerInput from 'samples/plate-maps/pattern-controls/PatternAssignmentIntegerInput';
import PatternDirectionDropdown from 'samples/plate-maps/pattern-controls/PatternDirectionDropdown';
import {
  BioentitySource,
  PlateMap,
  PlateMapProcedureContext,
  PlateModalAnalyticsMetadata,
  PreviewSourceError,
  ReagentAssignmentGroup,
  ReagentType,
  Source,
  SourceErrorsBySourceDilutionId,
  TestOrderInfoById,
  WellAssignmentMethod,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getSelectedRoleGroups,
  getSourceDilutionIdentifier,
  getSourceIdFromSourceDilutionIdentifier,
  wellHasTooManyDilutionFactors,
  wellHasTooManySources,
  wellHasTooManyTrialSamples,
} from 'samples/plate-maps/PlateMapUtil';
import {
  getDefaultMeasurementValidationErrors,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {isSourceContainerValidForTransfer} from 'samples/transfers/TransferUtil';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHTER, RED} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import PopoverIcon from 'ui-library/PopoverIcon';
import {InputChangeEvent} from 'util/Types';

import {FillPlateModalReducerActionType} from './machine/FillPlateModalConstants';

type ContainerNode = ExcludeInaccessible<InaccessibleContentsQuery['containers'][number]>;

export const INACCESSIBLE_CONTENTS_QUERY = gql`
  query InaccessibleContentsQuery($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
              name
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ARCHIVED_CONTENTS_QUERY = gql`
  query ArchivedContentsQuery($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
              name
              tagSchema {
                ... on TagSchemaNode {
                  id
                  name
                  iconId
                }
              }
              archiveRecord {
                purpose
              }
            }
          }
        }
      }
    }
  }
`;

export const TOO_MANY_CONTENTS_MESSAGE =
  'No more than 10 contents can be transferred to a plate well at once';
export const EMPTY_CONTAINER_MESSAGE = 'Containers with no contents cannot be used to fill a plate';
export const INACCESSIBLE_CONTENTS_MESSAGE =
  'Containers with inaccessible contents cannot be used to fill a plate';
export const INVALID_TRIAL_SAMPLES_MESSAGE =
  'Trial samples with no valid test orders cannot be used to fill a plate';

async function fetchArchivedContents(containerId: string): Promise<{
  archivedContents: Array<BioentitySource>;
  archivedSchemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}};
}> {
  const result = await doQuerySafe<ArchivedContentsQuery>(
    {query: ARCHIVED_CONTENTS_QUERY},
    {variables: {containerIds: [containerId]}}
  );
  if (result.containers.length === 0 || isInaccessible(result.containers[0])) {
    return {archivedContents: [], archivedSchemaDataById: {}};
  }
  const reagentContents: Array<BioentitySource> = [];
  const schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}} = {};
  for (const {containedItem} of result.containers[0].contents) {
    if (
      isInaccessible(containedItem) ||
      containedItem.archiveRecord == null ||
      containedItem.tagSchema == null
    ) {
      continue;
    }
    reagentContents.push({
      type: ReagentType.SOURCE_CONTAINABLE,
      id: containedItem.id,
      groupId: containedItem.tagSchema.id,
      transferQuantity: getDefaultMeasurementWithErrors(),
      sourceConcentration: getDefaultMeasurementWithErrors(),
      finalConcentration: getDefaultMeasurementWithErrors(),
    });
    schemaDataById[containedItem.tagSchema.id] = {
      name: containedItem.tagSchema.name,
      iconId: containedItem.tagSchema.iconId as BenchlingIconName,
    };
  }
  return {archivedContents: reagentContents, archivedSchemaDataById: schemaDataById};
}

function getInvalidTrialSamplesMessage(plateMapType: PlateMapType): string {
  if (plateMapType === PlateMapType.DESIGN) {
    return NO_TRIAL_SAMPLES_ON_PLATE_DESIGN_MESSAGE;
  } else {
    return INVALID_TRIAL_SAMPLES_MESSAGE;
  }
}

// Exported for testing
export function getApplySourcesPopoverErrorMessages({
  queryResultType,
  tooManyContents,
  inaccessibleContents,
  invalidTrialSamples,
  plateMapType,
}: {
  queryResultType: QueryResultType;
  tooManyContents: boolean;
  inaccessibleContents: boolean;
  invalidTrialSamples: boolean;
  plateMapType: PlateMapType;
}): Array<string> {
  const errorMessages: Array<string> = [];
  if (queryResultType === QueryResultType.PENDING) {
    errorMessages.push('Validating sources...');
  } else {
    if (queryResultType === QueryResultType.ERROR) {
      errorMessages.push('Validation failed, please remove and re-add sources');
    }
    if (tooManyContents) {
      errorMessages.push(TOO_MANY_CONTENTS_MESSAGE);
    }
    if (inaccessibleContents) {
      errorMessages.push(INACCESSIBLE_CONTENTS_MESSAGE);
    }
    if (invalidTrialSamples) {
      errorMessages.push(getInvalidTrialSamplesMessage(plateMapType));
    }
  }
  return errorMessages;
}

function FillPlateReagentPatternOptions({
  sendPlateModalMachineAction,
  patternParams,
  setPreviewWellRoles,
  assignByRoleGroups,
  onChangeAssignByRoleGroups,
  canAssignByRoleGroups,
}: {
  sendPlateModalMachineAction: PlateModalMachineAction;
  patternParams: PatternParametersInput;
  setPreviewWellRoles: (status: boolean) => void;
  assignByRoleGroups: boolean;
  onChangeAssignByRoleGroups: (canUse: boolean) => void;
  canAssignByRoleGroups: boolean;
}): JSX.Element {
  return (
    <div>
      <CheckboxInput
        className={css(styles.useRoleCheckbox)}
        label="Use role grouping to fill"
        id="fill-by-role-group"
        checked={assignByRoleGroups}
        onChange={(e: InputChangeEvent) => {
          onChangeAssignByRoleGroups(e.target.checked);
        }}
        disabled={!canAssignByRoleGroups}
      />
      {!assignByRoleGroups && (
        <div className={css(styles.patternControlsWrapper)}>
          <PatternDirectionDropdown
            selectedDirection={patternParams.fillDirection}
            onSelectDirection={(fillDirection) => {
              setPreviewWellRoles(false);
              sendPlateModalMachineAction({
                type: FillPlateModalReducerActionType.SET_PATTERN_PARAMS,
                patternParams: {
                  ...patternParams,
                  fillDirection,
                },
              });
            }}
            label="Fill direction"
            dataTestAttrs={{component: 'FillPlateReagentStep', element: 'fillDirectionDropdown'}}
          />
          <PatternAssignmentIntegerInput
            label="Replicates"
            value={patternParams.replicates}
            onChange={(replicates: number | null) => {
              setPreviewWellRoles(false);
              sendPlateModalMachineAction({
                type: FillPlateModalReducerActionType.SET_PATTERN_PARAMS,
                patternParams: {
                  ...patternParams,
                  replicates,
                },
              });
            }}
          />
          <PatternDirectionDropdown
            selectedDirection={patternParams.replicateDirection}
            onSelectDirection={(replicateDirection) => {
              sendPlateModalMachineAction({
                type: FillPlateModalReducerActionType.SET_PATTERN_PARAMS,
                patternParams: {
                  ...patternParams,
                  replicateDirection,
                },
              });
            }}
            label="Replicate direction"
          />
        </div>
      )}
    </div>
  );
}

export interface FillPlateReagentSettingsPaneProps {
  /** A function to dispatch change events to update the state props. */
  sendPlateModalMachineAction: PlateModalMachineAction;
  /** The plate map representing the plate being filled */
  initialPlateMap: PlateMap;
  /** How the wells will be filled by pattern */
  patternParams: PatternParametersInput;
  /** A preview of the plate as it is being filled, with preview reagents filled in to selected wells */
  previewPlateMap: PlateMap;
  /** What sources are currently being previewed, by source dilution id. Note that this is NOT the base
   * source id. */
  previewedSourcesBySourceDilutionId: {[sourceDilutionId: string]: Source};
  /** The ordering of previewed sources, i.e. for display under the fill controls */
  previewedSourceDilutionIdOrdering: Array<string>;
  /** Error messages for previewed sources to render in the reagent settings pane. */
  previewedSourceErrorsBySourceDilutionId: SourceErrorsBySourceDilutionId;
  /** Groupings of reagents being added together, e.g. when filling by pattern */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected */
  selectedWellIndices: Set<number>;
  /** Whether wells are being filled by group or by pattern */
  wellAssignmentMethod: WellAssignmentMethod;
  /** Whether the pattern that sources are assigned by is based on role groups */
  assignByRoleGroups: boolean;
  /** Whether the current well selection allows assigning sources by role group */
  canAssignByRoleGroups: boolean;
  /** Analytics metadata included with any analytics events */
  analyticsMetadata: PlateModalAnalyticsMetadata;
  /** Sets whether well role previews are toggled on or off */
  setPreviewWellRoles: (showPreview: boolean) => void;
  plateMapType: PlateMapType;
  /** Context relevant if in a procedure, including material inputs to enable adding planned values. */
  procedureContext: PlateMapProcedureContext;
}

/**
 * Renders the right side settings pane for assigning sources to a plate map.
 * @constructor
 */
export default function FillPlateReagentSettingsPane({
  sendPlateModalMachineAction,
  initialPlateMap,
  patternParams,
  previewPlateMap,
  previewedSourcesBySourceDilutionId,
  previewedSourceDilutionIdOrdering,
  previewedSourceErrorsBySourceDilutionId,
  reagentGroupsById,
  selectedWellIndices,
  wellAssignmentMethod,
  assignByRoleGroups,
  canAssignByRoleGroups,
  analyticsMetadata,
  setPreviewWellRoles,
  plateMapType,
  procedureContext,
}: FillPlateReagentSettingsPaneProps): JSX.Element {
  const applyWouldBeNoop = previewedSourceDilutionIdOrdering.length === 0 || selectedWellIndices.size === 0;

  const procedureTestOrderConfig = getProcedureTestOrderConfig({
    procedureStudyDesignType: procedureContext.procedureStudyDesignType,
    procedureId: procedureContext.procedureId,
    baseTestOrderConfig: procedureContext.procedureTestOrderConfig,
    procedureMethodExecutionInstanceId: procedureContext.procedureMethodExecutionInstanceId,
  });

  const previewHasWellsWithTooManyContents = useMemo(
    () =>
      previewPlateMap.wells.some(
        (well, index) => selectedWellIndices.has(index) && wellHasTooManySources(well)
      ),
    [selectedWellIndices, previewPlateMap]
  );

  const previewHasWellsWithTooManyTrialSamples = useMemo(
    () =>
      plateMapType !== PlateMapType.DESIGN &&
      previewPlateMap.wells.some(
        (well, index) => selectedWellIndices.has(index) && wellHasTooManyTrialSamples(well)
      ),
    [selectedWellIndices, previewPlateMap, plateMapType]
  );

  const previewHasWellsWithMultipleDilutions = useMemo(
    () =>
      plateMapType !== PlateMapType.DESIGN &&
      previewPlateMap.wells.some(
        (well, index) =>
          selectedWellIndices.has(index) &&
          wellHasTooManyDilutionFactors(well, previewPlateMap.testOrderDilutionInfoById)
      ),
    [selectedWellIndices, previewPlateMap, plateMapType]
  );

  const containerSources = Object.values(previewedSourcesBySourceDilutionId).filter(
    (source) => source.type === ReagentType.SOURCE_CONTAINER
  );
  const inaccessibleContentsResult = useSkippableBenchlingQuery<
    InaccessibleContentsQuery,
    InaccessibleContentsQueryVariables
  >(INACCESSIBLE_CONTENTS_QUERY, {
    skip: containerSources.length === 0,
    variables: {containerIds: containerSources.map((source) => source.id)},
  });
  const sourceIdsWithInaccessibleContents =
    inaccessibleContentsResult.type === QueryResultType.SUCCESS
      ? inaccessibleContentsResult.data.containers
          .filter((container): container is ContainerNode => !isInaccessible(container))
          .filter((accessibleContainer) =>
            accessibleContainer.contents.some((content) => isInaccessible(content.containedItem))
          )
          .map((accessibleContainerWithInaccessibleContent) => accessibleContainerWithInaccessibleContent.id)
      : [];
  const sourceIdsWithEmptyContainerError =
    inaccessibleContentsResult.type === QueryResultType.SUCCESS
      ? inaccessibleContentsResult.data.containers
          .filter(
            (container): container is ContainerNode =>
              !isInaccessible(container) &&
              !isSourceContainerValidForTransfer({
                ...container,
                containedItemIds: container.contents.map((content) =>
                  getAccessibleOrInaccessibleId(content.containedItem)
                ),
              })
          )
          .map((container) => container.id)
      : [];

  const hasBlockingErrors =
    previewHasWellsWithTooManyContents ||
    sourceIdsWithInaccessibleContents.length > 0 ||
    sourceIdsWithEmptyContainerError.length > 0 ||
    Object.keys(previewedSourceErrorsBySourceDilutionId[PreviewSourceError.INVALID_TRIAL_SAMPLE]).length > 0;

  // If banner type is not specified, defaults to error
  const bannerStackMessages: Array<{text: string; bannerType?: InlineBannerType}> = [];
  if (previewHasWellsWithTooManyContents) {
    bannerStackMessages.push({text: TOO_MANY_CONTENTS_MESSAGE});
  }
  if (sourceIdsWithEmptyContainerError.length > 0) {
    bannerStackMessages.push({text: EMPTY_CONTAINER_MESSAGE});
  }
  if (sourceIdsWithInaccessibleContents.length > 0) {
    bannerStackMessages.push({text: INACCESSIBLE_CONTENTS_MESSAGE});
  }
  if (
    Object.keys(previewedSourceErrorsBySourceDilutionId[PreviewSourceError.INVALID_TRIAL_SAMPLE]).length > 0
  ) {
    bannerStackMessages.push({text: getInvalidTrialSamplesMessage(plateMapType)});
  }
  if (previewHasWellsWithTooManyTrialSamples) {
    bannerStackMessages.push({
      text: TOO_MANY_TRIAL_SAMPLES_SELECTED_WELL_MESSAGE,
      bannerType: InlineBannerType.WARNING,
    });
  }
  if (previewHasWellsWithMultipleDilutions) {
    bannerStackMessages.push({
      text: TOO_MANY_DILUTION_FACTORS_SELECTED_WELL_MESSAGE,
      bannerType: InlineBannerType.WARNING,
    });
  }

  const shouldDisableApply =
    hasBlockingErrors ||
    ![QueryResultType.SUCCESS, QueryResultType.SKIPPED].includes(inaccessibleContentsResult.type);

  function removeSources(sourceDilutionIds: Array<string>): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.REMOVED_SOURCES, analyticsMetadata);
    sendPlateModalMachineAction({
      type: FillPlateModalReducerActionType.REMOVE_PREVIEWED_SOURCES,
      sourceDilutionIds,
    });
  }

  async function onSingleSelect(
    reagent: Source,
    schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}}
  ): Promise<void> {
    // Deselecting in search listing should remove all dilutions of that source
    const matchingSources = previewedSourceDilutionIdOrdering.filter(
      (selectedSourceDilutionId) =>
        getSourceIdFromSourceDilutionIdentifier(selectedSourceDilutionId) === reagent.id
    );
    if (matchingSources.length) {
      removeSources(matchingSources);
    } else {
      analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.ADDED_SOURCES, {
        ...analyticsMetadata,
        sourcesCount: 1,
        method: 'search',
      });

      let hydratedReagent: Source = reagent;
      let testOrderInfoById: TestOrderInfoById = {};
      let allSchemaDataById = schemaDataById;
      const expandedReagents: Array<Source> = [];
      const testOrderErrorsBySourceDilutionId: {[sourceDilutionId: string]: string} = {};

      if (reagent.type === ReagentType.SOURCE_CONTAINER) {
        const containerDataPromise = fetchContainerMeasurements([reagent.id]);
        const archivedContentsPromise = fetchArchivedContents(reagent.id);
        const [containerData, {archivedContents, archivedSchemaDataById}] = await Promise.all([
          containerDataPromise,
          archivedContentsPromise,
        ]);
        const allContents = [...reagent.contents, ...archivedContents];
        const quantity = containerData[reagent.id].quantity;
        hydratedReagent = {
          ...reagent,
          quantity: {value: quantity.value, unit: quantity.unit},
          contents: allContents.map((content) => {
            const concentration = containerData[reagent.id].contents[content.id].concentration;
            return {
              ...content,
              sourceConcentration: {
                measurement: {value: concentration.value, unit: concentration.unit},
                errors: getDefaultMeasurementValidationErrors(),
              },
            };
          }),
        };
        allSchemaDataById = {...archivedSchemaDataById, ...schemaDataById};
      }

      const testOrderResult = await maybeHydrateSourceWithFetchedTestOrders(
        hydratedReagent,
        procedureTestOrderConfig,
        plateMapType
      );
      testOrderInfoById = {...testOrderInfoById, ...testOrderResult.testOrderInfoById};
      expandedReagents.push(...testOrderResult.reagentsWithTestOrders);
      if (testOrderResult.sourceError != null) {
        for (const reagent of testOrderResult.reagentsWithTestOrders) {
          const sourceDilutionId = getSourceDilutionIdentifier(reagent, testOrderInfoById);
          testOrderErrorsBySourceDilutionId[sourceDilutionId] = testOrderResult.sourceError;
        }
      }

      sendPlateModalMachineAction({
        type: FillPlateModalReducerActionType.ADD_SOURCES_AND_SCHEMAS,
        reagents: expandedReagents,
        schemaDataById: allSchemaDataById,
        testOrderInfoById: procedureTestOrderConfig != null ? testOrderInfoById : null,
        testOrderErrorsBySourceDilutionId,
      });
    }
    setPreviewWellRoles(false);
  }

  const getSourceErrorIcons = (sourceDilutionId: string): JSX.Element => {
    const sourceId = getSourceIdFromSourceDilutionIdentifier(sourceDilutionId);
    const errorTexts: Array<string> = [];
    if (sourceIdsWithEmptyContainerError.includes(sourceId)) {
      errorTexts.push('This container does not have any contents to transfer');
    } else if (sourceIdsWithInaccessibleContents.includes(sourceId)) {
      errorTexts.push('This container holds inaccessible contents');
    } else if (
      previewedSourceErrorsBySourceDilutionId[PreviewSourceError.INVALID_TRIAL_SAMPLE][sourceDilutionId] !=
      null
    ) {
      errorTexts.push(
        previewedSourceErrorsBySourceDilutionId[PreviewSourceError.INVALID_TRIAL_SAMPLE][sourceDilutionId]
      );
    }
    if (errorTexts.length === 0) {
      return <></>;
    }
    return (
      <PopoverIcon
        icon="revision"
        className={css(styles.reagentChipError)}
        renderContent={() => errorTexts.join('. ')}
      />
    );
  };

  return (
    <div
      className={css(styles.reagentSourceView)}
      {...getTestProps({component: 'AddSourcesPane', element: 'root'})}
    >
      <div className={css(styles.searchHeaderText)}>
        Select wells in the plate, then add contents to them by defining a source
      </div>
      <InputWrapper id="fill-plate-reagent-search" label="Add sources" labelStyle={LabelStyle.LABEL}>
        <FillPlateSearchInput
          onSingleSelect={(reagent, schemaDataById) => {
            onSingleSelect(reagent, schemaDataById);
          }}
          onBulkAdd={(reagents, schemaDataById, testOrderInfoById) => {
            analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.ADDED_SOURCES, {
              ...analyticsMetadata,
              sourcesCount: reagents.length,
              method: 'worklist',
            });
            sendPlateModalMachineAction({
              type: FillPlateModalReducerActionType.ADD_SOURCES_AND_SCHEMAS,
              reagents,
              schemaDataById,
              testOrderInfoById,
            });
            setPreviewWellRoles(false);
          }}
          // Remove any dilution suffixes from the identifiers
          selectedApiIds={previewedSourceDilutionIdOrdering.map((sourceDilutionId) =>
            getSourceIdFromSourceDilutionIdentifier(sourceDilutionId)
          )}
          destinationPlateId={initialPlateMap.id}
          procedureContext={procedureContext}
          plateMapType={plateMapType}
        />
      </InputWrapper>
      <div className={css(styles.wellAssignmentMethodSelectorWrapper)}>
        <WellAssignmentMethodSelector
          wellAssignmentMethod={wellAssignmentMethod}
          setWellAssignmentMethod={(method: WellAssignmentMethod) => {
            sendPlateModalMachineAction({
              type: FillPlateModalReducerActionType.SET_WELL_ASSIGNMENT_METHOD,
              wellAssignmentMethod: method,
            });
            setPreviewWellRoles(false);
          }}
        />
      </div>
      {wellAssignmentMethod === WellAssignmentMethod.PATTERN && (
        <FillPlateReagentPatternOptions
          sendPlateModalMachineAction={sendPlateModalMachineAction}
          patternParams={patternParams}
          setPreviewWellRoles={setPreviewWellRoles}
          assignByRoleGroups={assignByRoleGroups}
          onChangeAssignByRoleGroups={(newAssignByRoleGroups: boolean) => {
            setPreviewWellRoles(newAssignByRoleGroups);
            sendPlateModalMachineAction({
              type: FillPlateModalReducerActionType.SET_ASSIGN_BY_ROLE_GROUPS,
              assignByRoleGroups: newAssignByRoleGroups,
            });
          }}
          canAssignByRoleGroups={canAssignByRoleGroups}
        />
      )}
      <div className={css(styles.reagentListWrapper)}>
        <div className={css(styles.reagentListWrapperHeader)}>
          <div className={css(styles.reagentCount)}>
            SOURCES {`(${previewedSourceDilutionIdOrdering.length})`}
          </div>
          <Button
            buttonStyle={ButtonStyle.LINK}
            text="Remove all sources"
            disabled={previewedSourceDilutionIdOrdering.length === 0}
            onClick={() => {
              removeSources(previewedSourceDilutionIdOrdering);
            }}
          />
        </div>
        {previewedSourceDilutionIdOrdering.length > 0 && (
          <div className={css(styles.reagentList)}>
            <div className={css(styles.scroll)}>
              {bannerStackMessages.length > 0 && (
                <div className={css(styles.bannerWrapper)}>
                  <InlineBannerStack
                    messages={bannerStackMessages.map(({text, bannerType}) => ({
                      bannerType: bannerType ?? InlineBannerType.ERROR,
                      text,
                    }))}
                  />
                </div>
              )}
              {wellAssignmentMethod === WellAssignmentMethod.SPECIFY_GROUP &&
                previewedSourceDilutionIdOrdering.map((sourceDilutionId) => (
                  <div key={sourceDilutionId} className={css(styles.reagentChipWrapper)}>
                    <ReagentChip
                      onRemove={() => {
                        removeSources([sourceDilutionId]);
                        setPreviewWellRoles(false);
                      }}
                      reagent={previewedSourcesBySourceDilutionId[sourceDilutionId]}
                      reagentGroupsById={reagentGroupsById}
                      testOrderInfoById={previewPlateMap.testOrderDilutionInfoById ?? {}}
                    />
                    {getSourceErrorIcons(sourceDilutionId)}
                  </div>
                ))}
              {wellAssignmentMethod === WellAssignmentMethod.PATTERN && (
                <OrderableReagentList
                  previewedReagentsBySourceDilutionId={previewedSourcesBySourceDilutionId}
                  previewedSourceDilutionIdOrdering={previewedSourceDilutionIdOrdering}
                  reagentGroupsById={reagentGroupsById}
                  onRemoveReagents={(sourceDilutionIds) => {
                    removeSources(sourceDilutionIds);
                    setPreviewWellRoles(false);
                  }}
                  onReorderReagents={(sourceDilutionIdOrdering) => {
                    sendPlateModalMachineAction({
                      type: FillPlateModalReducerActionType.REORDER_SOURCES,
                      sourceDilutionIdOrdering,
                    });
                    setPreviewWellRoles(false);
                  }}
                  orderedReagentLabels={
                    assignByRoleGroups ? getSelectedRoleGroups(initialPlateMap, selectedWellIndices) : null
                  }
                  testOrderInfoById={previewPlateMap.testOrderDilutionInfoById ?? {}}
                  getErrorForSourceDilutionId={getSourceErrorIcons}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div className={css(styles.addReagentButtonWrapper)}>
        <Popover
          isDisabled={!shouldDisableApply}
          renderContent={() => (
            <div>
              {getApplySourcesPopoverErrorMessages({
                queryResultType: inaccessibleContentsResult.type,
                tooManyContents: previewHasWellsWithTooManyContents,
                inaccessibleContents: sourceIdsWithInaccessibleContents.length > 0,
                invalidTrialSamples:
                  Object.keys(
                    previewedSourceErrorsBySourceDilutionId[PreviewSourceError.INVALID_TRIAL_SAMPLE]
                  ).length > 0,
                plateMapType,
              }).map((message, index) => (
                <div key={index}>{message}</div>
              ))}
            </div>
          )}
        >
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            onClick={() => {
              sendPlateModalMachineAction({type: FillPlateModalReducerActionType.APPLY_SOURCES});
              setPreviewWellRoles(false);
            }}
            text={`Add ${pluralize(previewedSourceDilutionIdOrdering.length, 'source')}`}
            size={ButtonSize.MEDIUM}
            disabled={applyWouldBeNoop || shouldDisableApply}
            dataTestAttrs={{component: 'Button', key: 'applyReagents'}}
          />
        </Popover>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  reagentSourceView: {
    display: 'flex',
    flexDirection: 'column',
    width: '420px',
    minWidth: '420px',
    flex: '0 0 420px',
  },
  searchHeaderText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: '16px',
  },
  wellAssignmentMethodSelectorWrapper: {
    margin: '16px 0px',
  },
  useRoleCheckbox: {
    marginBottom: '16px',
  },
  patternControlsWrapper: {
    marginBottom: '16px',
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 2fr',
    gridGap: '16px',
  },
  reagentListWrapper: {
    backgroundColor: GRAY_LIGHTER,
    paddingTop: '16px',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  reagentListWrapperHeader: {
    marginBottom: '16px',
    padding: '0px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bannerWrapper: {
    marginBottom: '16px',
  },
  reagentList: {
    overflow: 'hidden',
    flex: '1 1 auto',
  },
  scroll: {
    padding: '0px 16px 16px 16px',
    overflow: 'auto',
    height: '100%',
  },
  reagentCount: {
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  reagentChipWrapper: {
    ':not(:last-child)': {
      marginBottom: '8px',
    },
    display: 'flex',
    alignItems: 'center',
  },
  reagentChipError: {
    color: RED,
    width: '18px',
    height: '18px',
    marginRight: '4px',
  },
  addReagentButtonWrapper: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
